/**
 * PatchSettingsService - Manages patch-specific settings
 * 
 * Handles settings that are stored per-patch (like cable style).
 * These settings are serialized with patch data, not global preferences.
 * Uses AppState for centralized state management.
 */

import { appState } from './AppState.js';

/**
 * Creates a PatchSettingsService instance
 * 
 * @returns {Object} PatchSettingsService instance
 */
export function createPatchSettingsService() {
    // Initialize patch settings in appState
    appState.set('patch.settings', {
        cableStyle: 'curvy'  // Always patch-specific, no global fallback
    });

    /**
     * Get current patch settings for serialization
     * @returns {Object} Current patch settings
     */
    function getPatchSettings() {
        const settings = appState.get('patch.settings') || {};
        return {
            cableStyle: settings.cableStyle || 'curvy'
        };
    }

    /**
     * Apply loaded patch settings
     * @param {Object} settings - Settings from patch data
     */
    function applyPatchSettings(settings) {
        // Apply cable style if present, default to 'curvy'
        appState.set('patch.settings', {
            cableStyle: settings?.cableStyle || 'curvy'
        });
    }

    /**
     * Get effective cable style for this patch
     * @returns {string} Current cable style
     */
    function getEffectiveCableStyle() {
        const settings = appState.get('patch.settings') || {};
        return settings.cableStyle || 'curvy';
    }

    /**
     * Set a specific setting value
     * @param {string} key - Setting key
     * @param {any} value - Setting value
     */
    function setSetting(key, value) {
        if (key === 'cableStyle' && value !== undefined) {
            const settings = appState.get('patch.settings') || {};
            appState.set('patch.settings', { ...settings, cableStyle: value });
        }
    }

    /**
     * Get raw settings object (for direct access)
     * @returns {Object} Raw patch settings
     */
    function getRawSettings() {
        return appState.get('patch.settings') || { cableStyle: 'curvy' };
    }

    return {
        getPatchSettings,
        applyPatchSettings,
        getEffectiveCableStyle,
        setSetting,
        getRawSettings
    };
}
